/**
 * Connected without live data for longer than this: the feed shows as delayed (or as still reconnecting, right after a
 * connect). Spot symbols tick every second.
 */
export const delayedAfterMs = 15_000;

/** How often the feed health is checked again while something shows it. */
export const healthCheckMs = 2_000;

/** The longest wait between two reconnect attempts. */
export const maxRetryDelayMs = 30_000;

/** A start that has not finished by then is abandoned and tried again (a phone network can hang without failing). */
export const startTimeoutMs = 15_000;

/**
 * A snapshot whose newest price is older than this (by Binance's event time) comes from a server whose own stream has
 * stalled: its prices are not current.
 */
export const stalledSnapshotMs = 60_000;

/**
 * The wait before reconnect attempt number `attempt`, counted from 0: right away, then 1 s, 2 s, 4 s, 8 s, 16 s, and
 * 30 s from then on, forever.
 */
export function retryDelayMs(attempt: number): number {
  if (!(attempt > 0)) return 0;
  return Math.min(maxRetryDelayMs, 1000 * 2 ** Math.min(attempt - 1, 20));
}

/**
 * Whether a snapshot comes from a stalled server stream: its NEWEST event time (Unix ms, Binance's clock) is more than
 * `stalledSnapshotMs` behind `now`. Judged on the newest item only: an illiquid symbol's old event time is still its
 * current price. Without any event time (an older API) the snapshot counts as fresh.
 */
export function isSnapshotStalled(items: readonly { eventTime?: number | undefined }[], now: number): boolean {
  let newest = -Infinity;
  for (const item of items) {
    const eventTime = item.eventTime;
    if (typeof eventTime === 'number' && Number.isFinite(eventTime) && eventTime > 0) newest = Math.max(newest, eventTime);
  }

  return newest !== -Infinity && now - newest > stalledSnapshotMs;
}
